import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Progress } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AiOutlineClose as X,
  AiOutlineDownload as Download,
} from "react-icons/ai";
import { FaSearchMinus, FaSearchPlus } from "react-icons/fa";
import { FaDownload } from "react-icons/fa6";
import { TfiLayoutWidthDefault } from "react-icons/tfi";
import { isVideoFile, standardizeVolume } from "../../utils/Utils";
import { CustomAvatar } from "../avatar/CustomAvatar";
import ImageDisplay from "../chat/ImageDisplay";
import "./style.scss";

import { LuRotateCcw, LuRotateCw } from "react-icons/lu";
import { TbFlipHorizontal, TbFlipVertical } from "react-icons/tb";
import { useInfoUser } from "../../store/UserStore";
import { useGlobalDownloader } from "../../context/global/DownloadProvider";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";

export default function MediaStorageModal({ file, onClose, senderId }) {
  const { languageMap } = useInfoUser();
  const activeRef = useRef(null);
  const scrollRef = useRef(null);
  const [scaleImg, setScaleImg] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);

  const [sender, setSender] = useState({});
  const { orgList } = useAuthLayoutContext();

  const imgContainerRef = useRef(null);

  const isVideo = useMemo(() => isVideoFile(file?.fileName), [file?.fileName]);

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // 1. Sử dụng Global Downloader thay vì local
  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  // 2. Lấy state tải của file hiện tại đang xem
  const [fileState, setFileState] = useState({});
  const fileNameToSave = file?.fileName || "Willo-media";

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(file?.resourceId, fileNameToSave);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(file?.resourceId);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(file?.resourceId, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(file?.resourceId);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-2">
            <button onClick={handleResumeDownload}>
              <PlayCircleOutlined
                style={{ fontSize: "20px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <button onClick={handlePauseDownload}>
            <PauseOutlined style={{ fontSize: "20px", color: "white" }} />
          </button>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload}>
            <FaDownload style={{ fontSize: "20px", color: "white" }} />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-2">
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload}>
            <Download style={{ fontSize: "25px", color: "white" }} />
          </button>
        );
    }
  }, [fileState?.status]);

  const handleMouseDown = (e) => {
    if (scaleImg > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePos.x,
        y: e.clientY - imagePos.y,
      });

      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setImagePos({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });

      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    if (!imgContainerRef) return;

    setScaleImg((prevScale) => {
      const newScale = Math.min(prevScale + 0.5, 10);

      if (newScale <= 1) {
        setImagePos({
          x: 0,
          y: 0,
        });
      }

      return newScale;
    });
  };

  const handleZoomOut = () => {
    if (!imgContainerRef) return;

    setScaleImg((prevScale) => {
      const newScale = Math.max(prevScale - 0.3, 0.1);

      if (newScale <= 1) {
        setImagePos({ x: 0, y: 0 });
      }

      return newScale;
    });
  };

  const handleCenterLayout = () => {
    setImagePos({ x: 0, y: 0 });
    setScaleImg(1);
  };

  const handleWheel = (e) => {
    if (!imgContainerRef) return;

    setScaleImg((prevScale) => {
      const zoomIntensity = 0.1;
      const newScale =
        e.deltaY < 0
          ? prevScale + zoomIntensity
          : Math.max(prevScale - zoomIntensity, 0.1);

      if (newScale <= 1) {
        setImagePos({ x: 0, y: 0 });
      }
      return Math.min(newScale, 10);
    });
  };

  const handleRotateLeft = () => setRotation((prev) => prev - 90);
  const handleRotateRight = () => setRotation((prev) => prev + 90);

  const handleFlipX = () => setFlipX((prev) => !prev);
  const handleFlipY = () => setFlipY((prev) => !prev);

  // 3. Đã XÓA useEffect chứa saveFile() ngầm định

  useEffect(() => {
    // ĐÃ BỎ `cancelDownload()` Ở ĐÂY để tải ngầm không bị mất khi vuốt sang ảnh khác
    setScaleImg(1);
    setRotation(0);
    setFlipX(false);
    setFlipY(false);
    if (activeRef.current) {
      activeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [file]);

  useEffect(() => {
    setSender(
      orgList?.find((org) => org?.userCode === senderId || file?.userId),
    );
  }, [senderId, file?.userId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setFileState(downloads[file?.resourceId]);
  }, [downloads]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-3 bg-neutral-900 text-white text-sm">
        <span className="font-medium"></span>
        <button
          onClick={onClose}
          className="p-1 hover:bg-neutral-700 rounded transition"
        >
          <X size={20} />
        </button>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main viewer */}
        <div
          className="flex-1 flex justify-center items-center bg-black overflow-hidden"
          // onClick={onClose}
        >
          {file?.contentType === "VIDEO" ? (
            <div
              className="display-imgage h-[100%]"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={file?.mainPath || file?.path}
                controls
                autoPlay
                width="100%"
                controlsList="nodownload"
                className="img-preview h-[100%] pb-[30px]"
              >
                The browser does not support the video tag.
              </video>
            </div>
          ) : (
            <button
              ref={imgContainerRef}
              className="display-imgage h-[100%]"
              onClick={(e) => e.stopPropagation()}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div
                className="h-[100%] flex justify-center items-center"
                style={{
                  transform: `
                    translate(${imagePos.x}px, ${imagePos.y}px)
                    scale(${scaleImg})
                    rotate(${rotation}deg)
                    scaleX(${flipX ? -1 : 1})
                    scaleY(${flipY ? -1 : 1})
                  `,
                  transformOrigin: "center center",
                  transition: "transform 0.15s ease-out",
                  willChange: "transform",
                }}
              >
                <ImageDisplay
                  path={file?.mainPath || file?.path}
                  imageStyle={{
                    maxHeight: "100%",
                    width: "auto",
                    objectFit: "cover",
                    display: "block",
                    background: "white",
                  }}
                  classNameCss="img-preview"
                  showLoading={false}
                />
              </div>
            </button>
          )}

          <div
            className="absolute bottom-[65px] mr-[130px] left-0 right-0 flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-gray-300 text-sm bg-black/60 px-3 py-1 rounded-md max-w-[80%] truncate">
              {file?.originalName || file?.fileName || file?.resourceId}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex pr-[130px] items-center justify-between p-3 bg-neutral-900 text-gray-300 text-sm">
        <div className="flex items-center gap-2 min-w-[250px]">
          <CustomAvatar person={sender} pos={-1} styleCss={{}} />
          <div>
            <p className="font-medium leading-tight">{sender?.name}</p>
            <p className="text-xs">{formatDateTime(file?.createdAt)}</p>
          </div>
        </div>

        <div className="action-image flex items-center gap-2">
          <div className="flex justify-center gap-[10px]">
            <div className="icon-detail cursor-pointer flex gap-5 ">
              {!file?.videoImgResourceId && (
                <>
                  <button
                    className="icon-detail p-1 hover:text-white transition"
                    onClick={handleZoomOut}
                  >
                    <FaSearchMinus size={20} />
                  </button>
                  <button
                    className="icon-detail p-1 hover:text-white transition"
                    onClick={handleCenterLayout}
                  >
                    <TfiLayoutWidthDefault size={20} />
                  </button>
                  <button
                    className="icon-detail p-1 hover:text-white transition"
                    onClick={handleZoomIn}
                  >
                    <FaSearchPlus size={20} />
                  </button>

                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleRotateLeft}
                  >
                    <LuRotateCcw size={20} />
                  </button>
                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleRotateRight}
                  >
                    <LuRotateCw size={20} />
                  </button>

                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleFlipX}
                  >
                    <TbFlipHorizontal size={20} />
                  </button>
                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleFlipY}
                  >
                    <TbFlipVertical size={20} />
                  </button>

                  {/* <button className="icon-detail p-1 hover:text-white transition">
                      <Share2 size={25} />
                    </button> */}
                </>
              )}
              <div className="relative flex items-center">
                {renderDownloadButton()}

                {/* 4. Cập nhật DOWNLOAD_STATUS */}

                {[DOWNLOAD_STATUS.DOWNLOADING].includes(fileState?.status) && (
                  <div
                    className="absolute left-full gap-2 ml-2 top-1/2 -translate-y-1/2
                      flex items-center bg-black/40 px-2 py-[2px] rounded-md"
                  >
                    <div className="flex items-center">
                      <Progress
                        className="w-28 flex items-center"
                        percent={fileState?.progress}
                        showInfo={false}
                        strokeColor="#52c41a"
                        size="small"
                      />
                      <div className="text-[grey] text-[10px] ml-2 whitespace-nowrap">
                        {standardizeVolume(
                          (fileState?.progress * file?.volume) / 100,
                        )}{" "}
                        / {standardizeVolume(file?.volume)}
                      </div>
                    </div>

                    {[DOWNLOAD_STATUS.DOWNLOADING].includes(
                      fileState?.status,
                    ) && (
                      <button
                        className="flex items-center"
                        onClick={handleCancel}
                      >
                        <CloseOutlined
                          style={{ fontSize: "20px", color: "white" }}
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-[250px]"></div>
      </div>
    </div>
  );
}
